import { type SxProps, styled, type Theme } from '@mui/material/styles';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  type ChangeSingleCriteriaAction,
  type CriteriaDimension,
  type SortCriteria,
  SortDirection,
} from '@openx/types/tableCriteria.ts';
import type { BaseRow, Columns } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import { type JSX, useCallback } from 'react';

import { calculateStickyLeftOffset, calculateStickyRightProps } from './sections/utils.ts';
import { TableHeaderCell } from './TableHeaderCell.tsx';

export interface TableHeaderProps<RowT extends BaseRow> {
  columns: Columns<RowT>;
  onChange: ChangeSingleCriteriaAction<typeof CriteriaDimension.SORT>;
  criteria?: SortCriteria;
  stickyTable?: boolean;
  overrideTableCellStyles?: SxProps<Theme>;
  stickyActionColumnOffset?: number;
}

const StyledTableHeader = styled(TableRow)`
  .MuiTableCell-head {
    position: sticky;
    top: var(--topbar-height);
    z-index: 2;
  }
`;

export function TableHeader<RowT extends BaseRow>({
  columns,
  onChange,
  criteria,
  stickyTable,
  overrideTableCellStyles,
  stickyActionColumnOffset,
}: TableHeaderProps<RowT>): JSX.Element {
  const toggleSort = useCallback(
    (columnKey: string) => {
      if (!criteria?.sortableColumns.includes(columnKey)) {
        return;
      }

      const columnAlreadySortedDesc = criteria.column === columnKey && criteria.direction === SortDirection.ASC;
      const newDirection = columnAlreadySortedDesc ? SortDirection.DESC : SortDirection.ASC;
      onChange({
        ...criteria,
        column: columnKey,
        direction: newDirection,
      });
    },
    [criteria, onChange]
  );

  const TableHeaderWrapper = stickyTable ? StyledTableHeader : TableRow;

  const firstNonCheckboxIndex = columns.findIndex(column => column.key !== 'select');

  return (
    <TableHead data-test="table-header">
      <TableHeaderWrapper data-test="table-row">
        {columns.map((column, index) => {
          const { stickyRightOffset, isFirstStickyRight } = calculateStickyRightProps(
            column,
            index,
            columns,
            stickyActionColumnOffset
          );

          return (
            <TableHeaderCell
              column={column}
              toggleSort={criteria?.sortableColumns.includes(column.key) ? toggleSort : undefined}
              appliedSortDirection={criteria?.column === column.key ? criteria?.direction : undefined}
              key={column.key}
              overrideTableCellStyles={overrideTableCellStyles}
              overrideHeaderCellStyles={column.headerStyle}
              stickyLeftOffset={
                column.sticky === STICKY_POSITION.LEFT ? calculateStickyLeftOffset(columns, index) : undefined
              }
              stickyRightOffset={stickyRightOffset}
              isFirstNonCheckbox={firstNonCheckboxIndex === 0 && index === 0}
              isLeadingStickyRight={isFirstStickyRight}
            />
          );
        })}
      </TableHeaderWrapper>
    </TableHead>
  );
}
